/**
 * ProLayout `menuItemRender` / `subMenuItemRender` 包装：为路径层级 ≥2 的叶子项补图标（Pro 默认不解析 routes.icon 字符串）。
 * 与 {@link MenuRouteIcon}、路由 `routes.ts` 的 `icon` 字段配合。
 */
import type { MenuDataItem, ProLayoutProps } from '@ant-design/pro-components';
import { Tooltip } from 'antd';
import { Link, history } from '@umijs/max';
import React, { useEffect, useState } from 'react';
import { MenuRouteIcon } from './MenuRouteIcon';

type MenuItemRenderFn = NonNullable<ProLayoutProps['menuItemRender']>;
type SubMenuItemRenderFn = NonNullable<ProLayoutProps['subMenuItemRender']>;

/**
 * Pro 对嵌套较深的叶子项不会解析 `routes.icon` 字符串，可能直接显示字面量（如 image）。
 * 二级（/creative/image）与三级叶子均需走 MenuRouteIcon。
 */
function isMenuLeafWithRouteIcon(item: MenuDataItem): boolean {
  const p = item.path;
  if (!p || !item.icon || typeof item.icon !== 'string') return false;
  const depth = p.split('/').filter(Boolean).length;
  return depth >= 2;
}

/**
 * 文案始终渲染；收起时由 `global.less` 仅在 `.video-ui-sider-menu-inner` 内隐藏，
 * 悬停浮层（`.ant-menu-submenu-popup`）内仍显示名称。
 */
function buildIconTitle(item: MenuDataItem, collapsed?: boolean): React.ReactNode {
  return (
    <div
      className="ant-pro-base-menu-item-title"
      style={{
        display: 'flex',
        alignItems: 'center',
        columnGap: collapsed ? 0 : 10,
        justifyContent: collapsed ? 'center' : undefined,
      }}
    >
      <span
        className="ant-pro-base-menu-item-icon"
        style={{ display: 'inline-flex', lineHeight: 1 }}
      >
        <MenuRouteIcon icon={item.icon as string} />
      </span>
      <span className="ant-pro-base-menu-item-text ant-pro-base-menu-item-text-has-icon">
        {item.name}
      </span>
    </div>
  );
}

type MenuFormatMessage = (descriptor: {
  id: string;
  defaultMessage?: string;
}) => string;

function resolveMenuTooltipTitle(
  item: MenuDataItem,
  formatMessage?: MenuFormatMessage,
): string {
  const name = item.name != null ? String(item.name) : '';
  if (!formatMessage) return name;
  if (item.locale) {
    return formatMessage({ id: item.locale, defaultMessage: name });
  }
  return formatMessage({ id: `menu.${name}`, defaultMessage: name });
}

/**
 * 收起后 path 深度 ≥2 的叶子（如 /creative/image）。
 * `siderMenuType: 'group'` 时 Pro 会把二级项摊平到侧栏根级（level=1），不走 MenuItemTooltip。
 */
function isCollapsedNestedLeaf(item: MenuDataItem): boolean {
  const p = item.path;
  if (!p || item.children?.length || item.isUrl) return false;
  return p.split('/').filter(Boolean).length >= 2;
}

/** 对齐 Pro `MenuItemTooltip`：收起动画结束后再启 Tooltip，避免闪烁 */
function CollapsedMenuItemTooltip({
  title,
  collapsed,
  children,
}: {
  title: string;
  collapsed?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [delayedCollapsed, setDelayedCollapsed] = useState(collapsed);

  useEffect(() => {
    setOpen(false);
    const timer = window.setTimeout(() => setDelayedCollapsed(collapsed), 400);
    return () => window.clearTimeout(timer);
  }, [collapsed]);

  if (!delayedCollapsed || !title) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      title={title}
      placement="right"
      open={open}
      onOpenChange={setOpen}
    >
      <span
        className="video-ui-sider-menu-collapsed-tooltip-target"
        style={{ display: 'block', width: '100%' }}
      >
        {children}
      </span>
    </Tooltip>
  );
}

function wrapCollapsedMenuTooltip(
  title: string,
  collapsed: boolean | undefined,
  children: React.ReactNode,
): React.ReactNode {
  if (!collapsed || !title) return children;
  return (
    <CollapsedMenuItemTooltip title={title} collapsed={collapsed}>
      {children}
    </CollapsedMenuItemTooltip>
  );
}

/**
 * 与 @umijs/plugins layout 默认 menuItemRender 一致（app.tsx 覆盖 layout 后需自行保留）。
 * @see node_modules/@umijs/plugins/dist/layout.js
 */
function umiDefaultMenuItemRender(
  item: MenuDataItem & { isUrl?: boolean; target?: string },
  defaultDom: React.ReactNode,
): React.ReactNode {
  if (item.isUrl || item.children?.length) {
    return defaultDom;
  }
  const path = item.path;
  if (path && history.location.pathname !== path) {
    return (
      <Link
        to={path.replace('/*', '')}
        target={item.target}
        style={{ color: 'inherit', textDecoration: 'none', display: 'block' }}
      >
        {defaultDom}
      </Link>
    );
  }
  return defaultDom;
}

/**
 * 在保留上一段 `menuItemRender` 的前提下，为二级/三级叶子补图标，并保证可跳转。
 */
export function wrapMenuItemRenderWithDeepIcon(
  prev: MenuItemRenderFn | undefined,
): MenuItemRenderFn {
  return (item, defaultDom, menuProps) => {
    const data = item as MenuDataItem;

    let dom: React.ReactNode = defaultDom;
    if (
      !data.children?.length &&
      data.path &&
      !data.isUrl &&
      isMenuLeafWithRouteIcon(data)
    ) {
      dom = buildIconTitle(data, Boolean(menuProps?.collapsed));
    }

    let rendered = umiDefaultMenuItemRender(data, dom);
    if (prev) {
      rendered = prev(item, rendered, menuProps);
    }

    const collapsed = Boolean(menuProps?.collapsed);
    if (collapsed && isCollapsedNestedLeaf(data)) {
      const formatMessage = (menuProps as { formatMessage?: MenuFormatMessage })
        ?.formatMessage;
      rendered = wrapCollapsedMenuTooltip(
        resolveMenuTooltipTitle(data, formatMessage),
        collapsed,
        rendered,
      );
    }

    return rendered;
  };
}

function renderSubMenuTitleDom(
  item: MenuDataItem & { isUrl: boolean },
  defaultTitle: React.ReactNode,
  prev: SubMenuItemRenderFn | undefined,
  menuProps: Record<string, unknown>,
): React.ReactNode {
  let dom = prev ? prev(item, defaultTitle, menuProps) : defaultTitle;
  const icon = item.icon;
  if (
    typeof icon === 'string' &&
    icon.startsWith('icon-') &&
    React.isValidElement(dom)
  ) {
    const ch = React.Children.toArray(
      (dom as React.ReactElement<{ children?: React.ReactNode }>).props.children,
    );
    if (ch.length >= 2) {
      const iconWrap = ch[0];
      const textWrap = ch[1];
      if (React.isValidElement(iconWrap) && React.isValidElement(textWrap)) {
        dom = React.cloneElement(dom, {}, [
          React.cloneElement(iconWrap, { key: 'icon-wrap' }, [
            <MenuRouteIcon key="route-icon" icon={icon} />,
          ]),
          textWrap,
        ]);
      }
    }
  }
  return dom;
}

/** 一级分组菜单的默认落地页（无独立 component 时取第一个子路由） */
function resolveSubMenuNavigatePath(item: MenuDataItem): string | undefined {
  if (!item.path) return undefined;
  if (item.path === '/creative') {
    const firstChild = item.children?.find((c) => c.path);
    return firstChild?.path ?? '/creative/image';
  }
  return item.path;
}

/** 一级分组/子菜单标题可点击跳转（如「创作」→ 首个子页） */
export function wrapSubMenuItemRenderWithNavigation(
  prev: SubMenuItemRenderFn | undefined,
): SubMenuItemRenderFn {
  return (item, defaultTitle, menuProps) => {
    const dom = renderSubMenuTitleDom(
      item as MenuDataItem & { isUrl: boolean },
      defaultTitle,
      prev,
      menuProps,
    );
    const data = item as MenuDataItem;
    const collapsed = Boolean(menuProps?.collapsed);
    const formatMessage = (menuProps as { formatMessage?: MenuFormatMessage })
      ?.formatMessage;
    const tooltipTitle = resolveMenuTooltipTitle(data, formatMessage);

    let linked = dom;
    const targetPath = resolveSubMenuNavigatePath(data);
    if (targetPath && history.location.pathname !== targetPath) {
      linked = (
        <Link
          to={targetPath}
          style={{ color: 'inherit', textDecoration: 'none', display: 'block' }}
        >
          {dom}
        </Link>
      );
    }

    return wrapCollapsedMenuTooltip(tooltipTitle, collapsed, linked);
  };
}

/** @deprecated 请使用 wrapSubMenuItemRenderWithNavigation */
export function wrapSubMenuItemRenderWithLocalIconfont(
  prev: SubMenuItemRenderFn | undefined,
): SubMenuItemRenderFn {
  return wrapSubMenuItemRenderWithNavigation(prev);
}
